import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
} from "@mui/material";
import { SimpleRte } from '../../../../components/EnhancedRte';
import React, {useEffect} from "react";
import {HighlightOff} from "@mui/icons-material";
import {writeStatusOptions} from "../../../messages/message_list";
import EnhancedSelect from "../../../../components/EnhancedSelect";
import EnhancedTextField from "../../../../components/EnhancedTextField";
import {createPost, utilGetPartners, replyPost} from "../../../../utils/utils";
import {StyledInputLabel} from "../../../../components/EnhancedAutocomplete";
import CatalogCheckbox from "../../../../components/EnhancedCheckbox";
import { DialogHeader, pageContentGap } from "../../../../theme";

interface MessagePost {
    Id?: string | number;
    ThreadId?: string | number;
    Sender?: string | number;
    [key: string]: unknown;
}

interface NewMessageProps {
    open: boolean;
    onClose?: () => void;
    coid?: string | number;
    posts?: MessagePost[];
    disableReply?: boolean;
    disableSend?: boolean;
    postId?: string | number;
    threadId?: string | number;
    partner?: string | number | { label: string; value: string | number } | null;
    onSent?: () => void;
    setError?: (err: unknown) => void;
    reference?: string | number;
    [key: string]: unknown;
}

export default function NewMessage(props: NewMessageProps) {
    const {open} = props;
    const {coid} = props;
    const {posts, disableReply, disableSend} = props;
    const {postId, threadId} = props;

    const [partners, setPartners] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const [reply, setReply] = React.useState('');
    const [replyEmpty, setReplyEmpty] = React.useState(true);
    const [subject, setSubject] = React.useState('');
    const [partner, setPartner] = React.useState(props.partner);
    const [postStatus, setPostStatus] = React.useState('New');
    const [requireAck, setRequireAck] = React.useState(false);

    const sendPost = async () => {
        const html = reply;
        let response = null;

        let post = posts.filter(r => r.Sender !== coid)[0];
        if (!post)
            post = posts[0];
        if ((post || postId) && postId !== undefined && threadId !== undefined) {
            let threadid = threadId || post.ThreadId || post.Id;
            response = await replyPost(String(postId || post.Id), String(threadid ?? ''), "POC", html, requireAck);
        } else {
            response = await createPost(requireAck, html, subject, postStatus, props.reference != null ? String(props.reference) : '', "POC", partner);
        }


        if (response === true)
            if(props.onSent) props.onSent();
        else
            props.setError(response);
    }

    const loadPartners = async () => {
        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;

        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        setPartners(sortedPartners);
        setLoading(false);
    }

    useEffect(() => {
        loadPartners();
    }, []);

    useEffect(() => {
        setSubject("");
        setReply('');
        setReplyEmpty(true);
        setPartner(props.partner);
        setPostStatus("New");
        setRequireAck(false);

        // eslint-disable-next-line
    }, [open]);

    return (
        <Dialog maxWidth='md' fullWidth open={open} onClose={() => props.onClose()}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: "12px"
                        }
                    }
                }}
        >
            <DialogTitle>
                <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item container style={{flex: 0.5, flexDirection: "column"}}>
                        <DialogHeader>
                            {((posts && posts.length === 0) || (threadId === undefined && postId === undefined)) ? "New Message" : "Reply"}
                        </DialogHeader>
                    </Grid>

                    <IconButton onClick={() => props.onClose()} size="large">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent style={{maxHeight: '400px', display: 'flex', flexDirection: 'column'}}>
                {((posts && posts.length === 0) || (threadId === undefined && postId === undefined)) &&
                    <Grid container style={{marginBottom: '1em', gap: pageContentGap, flex: 1}}>
                        <Grid container>
                            <EnhancedTextField style={{flex: 1, marginRight: '1em'}} label='Subject'
                                               disabled={disableSend}
                                               value={subject} onChange={(e) => setSubject(e.target.value)}/>
                            <EnhancedSelect disabled={disableReply} style={{flex: 1, alignSelf: 'flex-end'}}
                                            variant='outlined' label='Status'
                                            value={{label: postStatus, value: postStatus}}
                                            options={writeStatusOptions} onChange={(e) => {
                                setPostStatus(e ? e.value : '')
                            }}/>
                        </Grid>

                        {props.noref && (
                            <Grid container flex={1}>
                                <EnhancedSelect
                                    loading={loading}
                                    label={"Partner"}
                                    value={{
                                        value: partner,
                                        label: partners.find(p => p.Id === partner)?.CompanyName ?? ""
                                    }}
                                    options={partners.map(partner => ({
                                        value: partner.Id,
                                        label: partner.CompanyName
                                    }))}
                                    onChange={(e) => {
                                        if (e == null)
                                            setPartner("");
                                        else
                                            setPartner(e.value);
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                }

                <StyledInputLabel>
                    Message
                </StyledInputLabel>
                <SimpleRte className='textEditor' value={reply} disabled={disableReply}
                                onChange={(html, isEmpty) => { setReply(html); setReplyEmpty(isEmpty); }}/>

                {((posts && posts.length === 0) || (threadId === undefined && postId === undefined)) && (
                    <Grid container alignItems={"center"}>
                        <StyledInputLabel style={{alignSelf: "center"}}>
                            Require Acknowledgement:
                        </StyledInputLabel>
                        <CatalogCheckbox
                            value={requireAck}
                            onChange={(e) => setRequireAck(e.target.checked)}
                        />
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="text" disabled={replyEmpty} onClick={sendPost}>
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}
